/* global globalThis */
import { clearSession } from "../shared/_helpers/session";
import React, { useEffect, useState } from "react";
import moment from "moment";
import momentTz from "moment-timezone";
import { useNavigate } from "react-router-dom";
import StarsIcon from "../styles/StarsIcon";
import { Button, DatePicker, Dialog, DialogActions, DialogClose, DialogContent, DialogDescription, DialogTitle, Textarea } from "./ui";
import { isPaymentConfirmed, isUnauthorized, paymentRefusalMessage, rememberUnconfirmedPayment } from "../shared/_helpers/paymentErrors";
import { bookAppointment, getSlotsForAppointment,getProfile, createOneTimeIntent, saveOneTimePayment, AddOnMeeting } from "../shared/_services/common.services";
import { bookingRefusalMessage, canAffordMeetings, meetingsAvailableCount } from "../shared/_helpers/meetingEntitlement";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { setCardContentType } from "../redux/stripeReducer";


const BookAppointmentModal = ({ onClose }) => {
    const [startDate, setStartDate] = useState(new Date());
    const [thankYouPopupShow, setThankYouPopupShow] = useState(false);
    const [appointmentBookingPopupShow, setAppointmentBookingPopupShow] = useState(true);
    const [isMeetingWarningPopupOpen, setIsMeetingWarningPopupOpen] = useState(false);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [query, setQuery] = useState("");
    const [appointmentDetails, setAppointmentDetails] = useState({});
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [isExtendedDuration, setIsExtendedDuration] = useState(false);
    const [userData,setUserData] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const [payaddonmeet, setPayAddOnMeet] = useState("");
    const [pendingMeeting, setPendingMeeting] = useState("");
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const dispatch = useDispatch();
    const [addOnMeetingPrices, setAddOnMeetingPrices] = useState({
        oneAdditionalMeetingPrice: "",
        twoAdditionalMeetingPrice: ""
    });
          


    const userId = JSON.parse(localStorage.getItem("currentUser"))?.user?._id;
    const getSlots = async () => {
        let body = {};

        body.date = moment(startDate).format('YYYY-MM-DD')
        body.timezone = momentTz.tz.guess(true);
        body.clientId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
        const data = await getSlotsForAppointment(body);
        if (data.status === 200) {
            if (data.data?.slots?.length > 0) {
                setAvailableSlots([...data.data.slots]);
            } else {
                setAvailableSlots([]);
                enqueueSnackbar(data.data?.message || "No slots available.", { variant: "warning" });
            }
        }
        else {
            if (data?.response?.status === 401) {
                clearSession();
                enqueueSnackbar("Your session has been expired!", {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                });
                navigate('/login');
            }
        }
    }
    const getUserData = async()=>{
        const data =  await getProfile(userId);
        if (data.status === 200) {
        setUserData(data?.data?.data?.user);
    }
    }

    const getAddOnMeeting = async () => {
            const data = await AddOnMeeting();
            if (data.status === 200 && data.data) {
              const meetingData = data.data;
              setAddOnMeetingPrices({
                oneAdditionalMeetingPrice: meetingData.oneAdditionalMeetingPrice,
                twoAdditionalMeetingPrice: meetingData.twoAdditionalMeetingPrice,
              });
            }
    };

    useEffect(() => {
        getSlots();
        getUserData();
        
    }, [startDate]); //eslint-disable-line

    useEffect(()=>{
    getAddOnMeeting();
    },[])

    // The five keys `createAppointment` actually reads. `consumed_meetings` used to
    // travel with the booking so the server could store the client's own arithmetic
    // as the new used-count; backend ARCH-036 deleted that counter and made the
    // booking path claim the allowance itself through `spendMeetings`, so the field
    // is no longer read. `clientId` went the same way earlier — it used to decide
    // whose quota was charged, which let any authenticated caller book against
    // someone else's balance (SEC-057), and the handler takes `req.userId` now and
    // ignores the body's value. Both are dropped rather than left in place: a key
    // the server discards is one this screen only looks like it controls.
    const buildAppointmentBody = () => ({
        date: moment(startDate).format("YYYY-MM-DD"),
        time: selectedSlot,
        duration: isExtendedDuration ? 60 : 30,
        query,
        timezone: momentTz.tz.guess(true),
    });

    const persistPendingAppointment = (body) => {
        globalThis.localStorage.setItem("pendingAppointment", JSON.stringify(body));
    };

    const onBookingSucceeded = (data) => {
        globalThis.localStorage.removeItem("pendingAppointment");
        setShowLoader(false);
        setAppointmentDetails(data?.data);
        setThankYouPopupShow(true);
        setAppointmentBookingPopupShow(false);
    };

    // `data?.data?.message` read the 2xx shape, so every refusal that arrives as an
    // axios error — which is all of them, since `bookAppointment` resolves rather
    // than throws — produced a warning toast with nothing written in it. Backend
    // ARCH-036's 403 and 409 land here.
    const onBookingFailedResponse = (result) => {
        setShowLoader(false);
        enqueueSnackbar(bookingRefusalMessage(result), { variant: "warning" });
    };

    const handleBookAppointment = async () => {
        setShowLoader(true);
        if (!selectedSlot) {
            enqueueSnackbar("Please select a time slot!", { variant: "error" });
            setShowLoader(false);
            return;
        }

        // A 60-minute booking is two 30-minute meetings — the server's UNIT-001,
        // and the same mapping the add-on price table below uses.
        const meetingsToConsume = isExtendedDuration ? 2 : 1;

        // Backend ARCH-036 replaced `total_meetings - consumed_meetings` with one
        // computed balance. The old subtraction did not merely go stale when those
        // fields were removed, it failed open: `totalMeetings` became `undefined`,
        // `n > undefined` is `false`, and so `needsAddonMeetingWarning` could never
        // be true. That silently retired the paid add-on offer — a client with no
        // meetings left was sent straight to `bookAppointment` and, once the server
        // started refusing it, got a warning toast with no text in it instead of a
        // price. Both halves are read through `_helpers/meetingEntitlement`, which
        // is where the "`null` means unlimited" encoding is decoded exactly once.
        setPendingMeeting(meetingsAvailableCount(userData));
        setPayAddOnMeet(meetingsToConsume);

        const appointmentBody = buildAppointmentBody();
        const needsAddonMeetingWarning =
            Boolean(userData?.selected_plan) &&
            !canAffordMeetings(userData, meetingsToConsume);

        try {
            if (needsAddonMeetingWarning) {
                setShowLoader(false);
                setIsMeetingWarningPopupOpen(true);
                persistPendingAppointment(appointmentBody);
                return;
            }

            if (!userData?.selected_plan) {
                persistPendingAppointment(appointmentBody);
                dispatch(setCardContentType('PayAsYouGo'));
                // No `amount` in the router state. The payment screen used to
                // forward it as the PaymentIntent amount, so the price of an
                // add-on meeting travelled through the browser and an edited
                // value bought the meeting for a cent. Backend SEC-048 makes the
                // server price it from `duration` against the same platform
                // singleton `AddOnMeeting()` reads. The prices stay fetched here
                // because the warning copy below quotes them to the user; they
                // just no longer decide the charge.
                navigate('/make-payment', {
                    state: {
                        from: 'addtionalMeeting',
                        email: userData.email,
                        duration: isExtendedDuration ? 60 : 30,
                    },
                });
                return;
            }

            persistPendingAppointment(appointmentBody);
            const data = await bookAppointment(appointmentBody);

            if (data.status === 200) {
                onBookingSucceeded(data);
                return;
            }
            onBookingFailedResponse(data);
        } catch {
            setShowLoader(false);
        }
    }

    const handleModalClose = async () => {
        setStartDate(new Date());
        setQuery("");
        setIsExtendedDuration(false); // Reset the duration state
        setThankYouPopupShow(false);
        onClose()
    }

    const handleMessageClick = () => {
        navigate("/dashboard", {
          state: { actionType: "MESSAGE" }
        });
        onClose()
      };


    const createOTIntent = async()=>{
        setPaymentProcessing(true);
        const userEmail = JSON.parse(localStorage.getItem("currentUser"))?.user?.email;
        const priceId = JSON.parse(localStorage.getItem("currentUser"))?.user?.selected_plan?.id;
        let duration;
        if (pendingMeeting == 1 && payaddonmeet == 2) {
            duration = 30;
        } else if (payaddonmeet == 1) {
            duration = 30;
        } else if (payaddonmeet == 2) {
            duration = 60;
        }
        const res = await createOneTimeIntent(userEmail,duration,priceId);
        if(res?.data?.data?.paymentIntent?.status == "succeeded"){
            const pendingAppointment = JSON.parse(localStorage.getItem('pendingAppointment'));
            const transactionDetails = {
                ...res?.data?.data?.paymentIntent,
                transactionId: res?.data?.data?.paymentIntent?.id,
            };
            const newaddon = await saveOneTimePayment(userEmail, transactionDetails, duration)
            // #88: saveOneTimePayment resolves to the AxiosError on a non-2xx, which
            // is truthy; only a 200 means the credit landed. The card is already
            // charged, so a failure points at support rather than inviting a retry.
            if (!isPaymentConfirmed(newaddon)) {
                if (isUnauthorized(newaddon)) rememberUnconfirmedPayment();
                setPaymentProcessing(false);
                setIsMeetingWarningPopupOpen(false);
                enqueueSnackbar(paymentRefusalMessage(newaddon), { variant: "error" });
                return;
            }
            {

                const res = await bookAppointment(pendingAppointment);

                // The refused-booking branch this flow did not have. The customer
                // has paid and the meeting credit has already landed by the time we
                // get here, so a refusal used to leave them charged, with no
                // appointment, staring at a button stuck on "Payment processing" —
                // nothing reset the flag and nothing said a word. Backend ARCH-036
                // makes a 409 plausible at exactly this point, since the booking
                // races the credit the payment just wrote.
                //
                // Recovering from it has to route around this popup rather than
                // through it. Its "Continue" button calls `createOneTimeIntent`
                // again, which mints a *new* PaymentIntent and charges again — so
                // offering it as the retry for a 409 would sell a second meeting to
                // fix a booking the first one already paid for. Instead: close the
                // popup, and re-read the profile so the credit that just landed is
                // in `userData`. Booking again then takes the pre-check's
                // can-afford branch and spends the credit, with nothing charged.
                const refusal = bookingRefusalMessage(res);
                if (refusal) {
                    setPaymentProcessing(false);
                    setIsMeetingWarningPopupOpen(false);
                    await getUserData();
                    enqueueSnackbar(refusal, { variant: "error" });
                    return;
                }

                setAppointmentDetails(res.data)
                // Save appointment data to localStorage
                setThankYouPopupShow(true);
                setPaymentProcessing(false);

                globalThis.localStorage.removeItem("pendingAppointment");
            }
        }
        else{
            setPaymentProcessing(false);
        }

    }

    const meetingWarningMessage = (() => {
        if (pendingMeeting == 1 && payaddonmeet == 2) {
            return `You have one meeting left If you wish to schedule an additional meeting of 30 min, they will incur a charge of $${addOnMeetingPrices.oneAdditionalMeetingPrice} on a per-meeting basis.`;
        }

        if (payaddonmeet == 1) {
            return `You have reached the maximum number of meetings included in your plan. If you wish to schedule an additional meeting of 30 min, they will incur a charge of $${addOnMeetingPrices.oneAdditionalMeetingPrice} on a per-meeting basis.`;
        }

        if (payaddonmeet == 2) {
            return `You have reached the maximum number of meetings included in your plan. If you wish to schedule an additional meeting of 60 min, they will incur a charge of $${addOnMeetingPrices.twoAdditionalMeetingPrice} on a per-meeting basis.`;
        }

        return "";
    })();

    const dialogProps = {
        onInteractOutside: (e) => e.preventDefault(),
        onEscapeKeyDown: (e) => e.preventDefault(),
    };
    const closeButton = (
        <Button variant="ghost" size="sm" iconOnly aria-label="Close" className="tw-shrink-0">
            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
        </Button>
    );

    return (
        <>
            {/* Appointment booking popup */}
            {!thankYouPopupShow && <Dialog
                open={appointmentBookingPopupShow}
                onOpenChange={(open) => !open && setAppointmentBookingPopupShow(false)}
            >
                <DialogContent className="tw-max-w-3xl" {...dialogProps}>
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <DialogTitle>Want to talk it out? Let’s book a call.</DialogTitle>
                        <DialogClose asChild onClick={onClose}>
                            {closeButton}
                        </DialogClose>
                    </div>
                    <DialogDescription className="tw-mb-6">
                        If you prefer a real conversation, pick a time that works. Prefer texting? You can message your advisor anytime.
                    </DialogDescription>
                    <div className="tw-grid tw-gap-6 md:tw-grid-cols-2">
                        <div data-slot="appointment-date-paicker">
                            <DatePicker
                                inline
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                minDate={new Date()}
                            />
                        </div>
                        <div>
                            {availableSlots?.length > 0 && <h2 className="tw-m-0 tw-mb-3 tw-text-md tw-font-bold tw-text-text">Pick a time that works for you. </h2>}
                            <div data-slot="slot-list" className="tw-grid tw-max-h-64 tw-grid-cols-3 tw-gap-2 tw-overflow-y-auto sm:tw-grid-cols-4 md:tw-grid-cols-3">
                                {
                                    availableSlots?.length > 0 && availableSlots.map((data) => (
                                        <Button
                                            key={data}
                                            variant={selectedSlot === data ? "primary" : "quiet"}
                                            size="sm"
                                            aria-pressed={selectedSlot === data}
                                            data-slot="slot-list-item"
                                            onClick={() => {
                                                setSelectedSlot(data)
                                                if (data === availableSlots[availableSlots.length - 1]) {
                                                    setIsExtendedDuration(false);
                                                }
                                            }}
                                        >
                                            {data}
                                        </Button>
                                    ))
                                }
                            </div>

                            {availableSlots?.length === 0 &&
                            <div>
                                <h2 className="tw-m-0 tw-text-md tw-font-bold tw-text-text">No open times at the moment.</h2>
                                <p className="tw-mb-4 tw-mt-1 tw-text-sm tw-text-text-muted">But your advisor is still here — send a message and they’ll reply within 1 business day.</p>
                                <Button size="sm" onClick={() => handleMessageClick()}>Message Your Advisor Instead</Button>
                            </div>}
                        </div>
                    </div>
                    <Textarea
                        className="tw-my-6"
                        placeholder="Anything you want to cover on this call? Totally optional."
                        id="additional-info"
                        name="additional-info"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        rows="3"
                    />
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                        <small className="tw-text-xs tw-text-text-muted">*You can also pre-add 30 minutes when booking.</small>
                        <Button
                            onClick={handleBookAppointment}
                            disabled={showLoader}
                        >
                            {showLoader ? "Booking..." : "Book"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>}

            {/* thank you popup */}
            <Dialog open={thankYouPopupShow} onOpenChange={(open) => !open && handleModalClose()}>
                <DialogContent {...dialogProps}>
                    <div className="tw-flex tw-justify-end">
                        <DialogClose asChild>
                            {closeButton}
                        </DialogClose>
                    </div>

                    <DialogTitle>You’re all set!</DialogTitle>
                    <DialogDescription className="tw-mb-6">
                        Your call has been scheduled.<br></br>
                        When it’s time, you’ll join the video meeting directly from your Meetings tab in the STARs app.
                    </DialogDescription>
                    <div data-slot="appointment-dtls-box" className="tw-rounded-md tw-bg-surface-sunken tw-p-4">
                        <div className="tw-mb-4">
                            <p className="tw-m-0 tw-mb-1 tw-text-sm tw-text-text-muted">You're meeting with</p>
                            <strong className="tw-text-text">{appointmentDetails?.advisor?.firstName} {appointmentDetails?.advisor?.lastName}</strong>
                        </div>

                        <div className="tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <StarsIcon name="calendar" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted" />
                                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">Date & Time</p>
                            </div>
                            <div>
                                <p className="tw-m-0 tw-text-sm tw-text-text">{moment.utc(appointmentDetails?.appointment?.date).format('LL')}, {appointmentDetails?.appointment?.time}</p>
                            </div>
                        </div>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <StarsIcon name="info" className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-muted" />
                                <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-text">Details</p>
                            </div>
                            <div>
                                <p className="tw-m-0 tw-text-sm tw-text-text">We’ve sent and email with your booking details</p>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Meeting Warning popup */}
            {isMeetingWarningPopupOpen && <Dialog
                open={isMeetingWarningPopupOpen}
                onOpenChange={(open) => !open && setIsMeetingWarningPopupOpen(false)}
            >
                <DialogContent {...dialogProps}>
                    <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                        <StarsIcon name="alert" className="tw-h-8 tw-w-8 tw-shrink-0 tw-text-warning" />
                        <DialogClose
                            asChild
                            onClick={() => {
                                globalThis.localStorage.removeItem("pendingAppointment");
                            }}
                        >
                            {closeButton}
                        </DialogClose>
                    </div>
                    <DialogTitle className="tw-mt-2">{meetingWarningMessage}</DialogTitle>
                    <DialogDescription className="tw-sr-only">Additional meeting charge</DialogDescription>
                    <DialogActions className="tw-justify-center">
                        <Button onClick={() => createOTIntent()}>{paymentProcessing ? "Payment processing":"Continue"}</Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>}
        </>
    )
}

export default BookAppointmentModal;
